import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  OfficeBuildingIcon,
  LocationMarkerIcon,
  PhoneIcon,
  MailIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  CalendarIcon,
  ClipboardListIcon,
  CogIcon,
  XIcon,
  CodeIcon,
  KeyIcon,
} from "@heroicons/react/outline";
import { toast } from "react-toastify";
import { getBookingWidgetConfig } from "../../../../apis/channelManager";
import Overview from "./TabSets/Overview/overview";
import Availability from "./TabSets/Availability/availability";
import Reservations from "./TabSets/Reservations/reservations";
import Configurations from "./TabSets/Configurations/configurations";
import ReservationsPage from "../../reservations/ReservationsPage";
import BookingWidgetPage from "../../channelManager/BookingWidgetPage";
import { PmsCredentialsPanel } from "../../developer/DeveloperPage";

const escapeAttribute = (value) => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/"/g, "&quot;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;");

const buildWidgetPreviewDocument = (preview) => {
  if (!preview?.api_base || !preview?.script_url || !preview?.token) return "";

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Booking widget preview</title>
    <style>html,body{margin:0;padding:14px;background:#f8fafc}body{min-width:280px}</style>
  </head>
  <body>
    <div data-channelpro-booking
      data-property-key="${escapeAttribute(preview.public_key)}"
      data-api-base="${escapeAttribute(preview.api_base)}"
      data-preview-token="${escapeAttribute(preview.token)}"
      data-preview-expires="${escapeAttribute(preview.expires_at)}"></div>
    <script async src="${escapeAttribute(preview.script_url)}"></script>
  </body>
</html>`;
};

const ViewProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // detect user role to conditionally render some UI (breadcrumbs for supervisors)
  let userRole = null;
  try {
    const u = localStorage.getItem("user");
    if (u) {
      const userObj = JSON.parse(u);
      userRole = userObj?.role?.role_name || null;
    }
  } catch (e) {
    userRole = null;
  }
  const [resort, setResort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [widgetPreview, setWidgetPreview] = useState(null);
  const [widgetPreviewLoading, setWidgetPreviewLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("widget");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchResort = async () => {
      setLoading(true);
      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await axios.get(`${baseUrl}/resort/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setResort(response.data.data);
      } catch (error) {
        console.error("Error fetching resort:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResort();
  }, [baseUrl, id]);

  const tabs = [
    {
      id: "overview",
      name: "Overview",
      icon: ChartBarIcon,
      component: Overview,
    },
    {
      id: "availability",
      name: "Availability",
      icon: CalendarIcon,
      component: Availability,
    },
    {
      id: "bookings",
      name: "Bookings",
      icon: ClipboardListIcon,
      component: ReservationsPage,
    },
    {
      id: "reservations",
      name: "Reservations",
      icon: ClipboardListIcon,
      component: Reservations,
    },
    {
      id: "configurations",
      name: "Configurations",
      icon: CogIcon,
      component: Configurations,
    },
  ];

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  const openWidgetPreview = async () => {
    setWidgetPreviewLoading(true);
    try {
      const config = await getBookingWidgetConfig(resort.resort_code);
      if (!config?.enabled) {
        toast.error("Enable this property's booking widget in the settings panel before opening its preview.");
        return;
      }
      if (!config?.preview?.token) {
        toast.error("A preview session could not be created. Refresh and try again.");
        return;
      }
      setWidgetPreview({
        ...config.preview,
        public_key: config.public_key,
      });
      setSettingsOpen(false);
    } catch (error) {
      toast.error(error?.message || "Unable to load the booking widget preview");
    } finally {
      setWidgetPreviewLoading(false);
    }
  };

  const openSettings = (tab = "widget") => {
    setSettingsTab(tab);
    setSettingsOpen(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Loading property details...</p>
        <style jsx>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
            background: white;
            border-radius: 1rem;
            padding: 3rem;
          }

          .spinner {
            width: 3rem;
            height: 3rem;
            border: 4px solid #e5e7eb;
            border-top-color: #8b5cf6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          .loading-text {
            margin-top: 1rem;
            color: #6b7280;
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    );
  }

  if (!resort) {
    return (
      <div className="error-container">
        <OfficeBuildingIcon className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Property Not Found
        </h3>
        <p className="text-gray-600 mb-6">
          The property you're looking for doesn't exist.
        </p>
        <button
          onClick={() => navigate("/dashboard/property")}
          className="back-btn"
        >
          Go Back to Properties
        </button>
        <style jsx>{`
          .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
            background: white;
            border-radius: 1rem;
            padding: 3rem;
            text-align: center;
          }

          .back-btn {
            padding: 0.75rem 1.5rem;
            background: linear-gradient(135deg, #8b5cf6, #6366f1);
            color: white;
            border: none;
            border-radius: 0.75rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .back-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="view-property-page">
      {/* Breadcrumb - hide for resort-supervisor */}
      {userRole !== "resort-supervisor" && (
        <nav className="breadcrumb">
          <Link to="/dashboard" className="breadcrumb-item">
            <HomeIcon className="w-4 h-4" />
            <span>Dashboard</span>
          </Link>
          <svg className="breadcrumb-separator" viewBox="0 0 6 10">
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 9 4-4-4-4"
            />
          </svg>
          <Link to="/dashboard/property" className="breadcrumb-item">
            <span>Properties</span>
          </Link>
          <svg className="breadcrumb-separator" viewBox="0 0 6 10">
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 9 4-4-4-4"
            />
          </svg>
          <span className="breadcrumb-item-active">{resort.resort_name}</span>
        </nav>
      )}

      {/* Property Header */}
      <div className="property-header">
        <div className="header-gradient">
          <div className="header-content">
            {userRole !== "resort-supervisor" && (
              <button
                onClick={() => navigate("/dashboard/property")}
                className="back-button"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span>Back</span>
              </button>
            )}
            <div className="property-info">
              <div className="property-icon">
                <OfficeBuildingIcon className="w-10 h-10" />
              </div>
              <div className="property-details">
                <h1 className="property-name">{resort.resort_name}</h1>
                <p className="property-code">
                  Property ID: #{resort.resort_code}
                </p>
              </div>
            </div>

            <div className="header-actions">
              <button
                type="button"
                className="property-widget-button"
                onClick={() => openSettings("widget")}
                title="Configure and preview the booking widget"
              >
                <CodeIcon className="w-5 h-5" />
                <span>Booking widget</span>
              </button>
              <button
                type="button"
                className="property-settings-button"
                onClick={() => openSettings("widget")}
                title="Open property widget and API settings"
              >
                <CogIcon className="w-5 h-5" />
                <span>Settings</span>
              </button>
            </div>
          </div>

          {/* Property Quick Info */}
          <div className="quick-info">
            {resort.resort_phone && (
              <div className="info-item">
                <PhoneIcon className="info-icon" />
                <span>{resort.resort_phone}</span>
              </div>
            )}
            {resort.resort_email && (
              <div className="info-item">
                <MailIcon className="info-icon" />
                <span>{resort.resort_email}</span>
              </div>
            )}
            {resort.resort_address_one && (
              <div className="info-item">
                <LocationMarkerIcon className="info-icon" />
                <span>{resort.resort_address_one}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-container">
        <div className="tabs-wrapper">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              >
                <Icon className="tab-icon" />
                <span className="tab-label">{tab.name}</span>
                {activeTab === tab.id && (
                  <div className="active-indicator"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {ActiveComponent && <ActiveComponent resort={resort} />}
      </div>

      {settingsOpen && (
        <div className="property-settings-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSettingsOpen(false); }}>
          <section className="property-settings-modal" role="dialog" aria-modal="true" aria-labelledby="property-settings-title">
            <div className="property-settings-header">
              <div>
                <p className="property-settings-eyebrow">{resort.resort_name}</p>
                <h2 id="property-settings-title">Property integrations</h2>
                <p>Manage the guest-facing widget and credentials used by PMS integrations.</p>
              </div>
              <button type="button" className="property-settings-close" onClick={() => setSettingsOpen(false)} aria-label="Close property settings">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="property-settings-tabs" role="tablist" aria-label="Property integration settings">
              <button type="button" role="tab" aria-selected={settingsTab === "widget"} className={`property-settings-tab ${settingsTab === "widget" ? "active" : ""}`} onClick={() => setSettingsTab("widget")}>
                <CodeIcon className="w-4 h-4" /> Booking widget
              </button>
              <button type="button" role="tab" aria-selected={settingsTab === "api"} className={`property-settings-tab ${settingsTab === "api" ? "active" : ""}`} onClick={() => setSettingsTab("api")}>
                <KeyIcon className="w-4 h-4" /> PMS & API keys
              </button>
            </div>
            <div className="property-settings-content">
              {settingsTab === "widget" ? (
                <BookingWidgetPage fixedPropertyCode={resort.resort_code} fixedProperty={resort} onPreview={openWidgetPreview} previewLoading={widgetPreviewLoading} />
              ) : (
                <PmsCredentialsPanel fixedPropertyCode={resort.resort_code} fixedProperty={resort} />
              )}
            </div>
          </section>
        </div>
      )}

      {widgetPreview && (
        <div className="widget-preview-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setWidgetPreview(null); }}>
          <section className="widget-preview-modal" role="dialog" aria-modal="true" aria-labelledby="widget-preview-title">
            <div className="widget-preview-header">
              <div>
                <p className="widget-preview-eyebrow">External website simulation</p>
                <h2 id="widget-preview-title">Live booking widget</h2>
                <p>Run the complete availability, guest details, and reservation flow in an isolated website frame.</p>
              </div>
              <button type="button" className="widget-preview-close" onClick={() => setWidgetPreview(null)} aria-label="Close booking widget preview">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="widget-preview-toolbar">
              <span><span className="preview-status-dot" /> Live property widget</span>
              <span>Test bookings create real reservations · Session expires in 15 minutes</span>
            </div>
            <div className="widget-preview-frame-wrap">
              <iframe
                title="Booking widget preview"
                srcDoc={buildWidgetPreviewDocument(widgetPreview)}
                sandbox="allow-scripts allow-forms"
                className="widget-preview-frame"
              />
            </div>
          </section>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .view-property-page {
          padding: 0;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #6b7280;
          text-decoration: none;
          transition: color 0.2s;
        }

        .breadcrumb-item:hover {
          color: #8b5cf6;
        }

        .breadcrumb-separator {
          width: 0.75rem;
          height: 0.75rem;
          fill: none;
          stroke: #9ca3af;
        }

        .breadcrumb-item-active {
          font-weight: 600;
          color: #1f2937;
        }

        .property-header {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .header-gradient {
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        .header-gradient::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 300px;
          height: 300px;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 70%
          );
          border-radius: 50%;
          transform: translate(30%, -30%);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          position: relative;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .property-settings-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.7rem 1rem;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 0.65rem;
          background: rgba(255, 255, 255, 0.16);
          color: #ffffff;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
          backdrop-filter: blur(10px);
          transition: all 0.2s ease;
        }

        .property-settings-button:hover {
          background: rgba(255, 255, 255, 0.28);
          transform: translateY(-1px);
        }

        .property-widget-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.7rem 1rem;
          border: 1px solid rgba(255, 255, 255, 0.85);
          border-radius: 0.65rem;
          background: #ffffff;
          color: #4338ca;
          font-size: 0.85rem;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 6px 14px rgba(49, 46, 129, 0.16);
          transition: all 0.2s ease;
        }

        .property-widget-button:hover {
          background: #eef2ff;
          border-color: #e0e7ff;
          transform: translateY(-1px);
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 0.5rem;
          color: white;
          font-weight: 500;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateX(-4px);
        }

        .property-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex: 1;
        }

        .property-icon {
          width: 4rem;
          height: 4rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
        }

        .property-details {
          flex: 1;
        }

        .property-name {
          font-size: 1.75rem;
          font-weight: 800;
          color: white;
          margin: 0 0 0.25rem 0;
        }

        .property-code {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
        }

        .edit-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: white;
          border: none;
          border-radius: 0.75rem;
          color: #8b5cf6;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .edit-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
        }

        .quick-info {
          display: flex;
          flex-wrap: wrap;
          gap: 2rem;
          position: relative;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: white;
          font-size: 0.875rem;
        }

        .info-icon {
          width: 1.25rem;
          height: 1.25rem;
          opacity: 0.9;
        }

        .tabs-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          padding: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .tabs-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 0.5rem;
          padding: 0.5rem 0.25rem 0.25rem;
          border-top: 1px solid #f1f5f9;
        }

        .widget-preview-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 0.9rem;
          border: 1px solid #c7d2fe;
          border-radius: 0.65rem;
          background: #eef2ff;
          color: #4338ca;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .widget-preview-link:hover {
          background: #e0e7ff;
          border-color: #a5b4fc;
          transform: translateY(-1px);
        }

        .widget-preview-link:disabled {
          opacity: 0.6;
          cursor: wait;
          transform: none;
        }

        .tabs-wrapper {
          display: flex;
          gap: 0.5rem;
          overflow-x: auto;
        }

        .tabs-wrapper::-webkit-scrollbar {
          height: 4px;
        }

        .tabs-wrapper::-webkit-scrollbar-track {
          background: transparent;
        }

        .tabs-wrapper::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 2px;
        }

        .tab-button {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 1.5rem;
          background: transparent;
          border: none;
          border-radius: 0.75rem;
          color: #6b7280;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .tab-button:hover {
          background: #f9fafb;
          color: #374151;
        }

        .tab-button.active {
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .tab-icon {
          width: 1.25rem;
          height: 1.25rem;
        }

        .tab-label {
          font-weight: 600;
        }

        .active-indicator {
          position: absolute;
          bottom: -0.5rem;
          left: 50%;
          transform: translateX(-50%);
          width: 2rem;
          height: 0.25rem;
          background: white;
          border-radius: 9999px;
        }

        .tab-content {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          padding: 2rem;
          min-height: 400px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .property-settings-backdrop {
          position: fixed;
          z-index: 90;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: rgba(15, 23, 42, 0.68);
          backdrop-filter: blur(5px);
        }

        .property-settings-modal {
          display: flex;
          flex-direction: column;
          width: min(1220px, 100%);
          max-height: 94vh;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          border-radius: 1.25rem;
          background: #f8fafc;
          box-shadow: 0 30px 85px rgba(15, 23, 42, 0.34);
        }

        .property-settings-header {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          padding: 1.35rem 1.5rem;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
        }

        .property-settings-eyebrow {
          margin: 0 0 0.25rem;
          color: #4f46e5;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .property-settings-header h2 {
          margin: 0;
          color: #0f172a;
          font-size: 1.35rem;
          font-weight: 800;
        }

        .property-settings-header p:last-child {
          margin: 0.35rem 0 0;
          color: #64748b;
          font-size: 0.82rem;
        }

        .property-settings-close {
          align-self: flex-start;
          display: inline-flex;
          padding: 0.45rem;
          border: 0;
          border-radius: 0.5rem;
          background: #f1f5f9;
          color: #475569;
          cursor: pointer;
        }

        .property-settings-close:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        .property-settings-tabs {
          display: flex;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem 0;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
        }

        .property-settings-tab {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.7rem 0.9rem;
          border: 0;
          border-bottom: 2px solid transparent;
          background: transparent;
          color: #64748b;
          font-size: 0.82rem;
          font-weight: 800;
          cursor: pointer;
        }

        .property-settings-tab.active {
          border-bottom-color: #4f46e5;
          color: #4338ca;
        }

        .property-settings-content {
          min-height: 0;
          overflow: auto;
          padding: 1.25rem;
        }

        .widget-preview-backdrop {
          position: fixed;
          z-index: 80;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.25rem;
          background: rgba(15, 23, 42, 0.68);
          backdrop-filter: blur(5px);
        }

        .widget-preview-modal {
          display: flex;
          flex-direction: column;
          width: min(920px, 100%);
          max-height: min(900px, 94vh);
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 1.25rem;
          background: #ffffff;
          box-shadow: 0 30px 80px rgba(15, 23, 42, 0.35);
        }

        .widget-preview-header {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .widget-preview-eyebrow {
          margin: 0 0 0.25rem;
          color: #4f46e5;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .widget-preview-header h2 {
          margin: 0;
          color: #0f172a;
          font-size: 1.25rem;
          font-weight: 800;
        }

        .widget-preview-header p:last-child {
          margin: 0.35rem 0 0;
          color: #64748b;
          font-size: 0.8rem;
        }

        .widget-preview-close {
          align-self: flex-start;
          display: inline-flex;
          padding: 0.45rem;
          border: 0;
          border-radius: 0.5rem;
          background: #f1f5f9;
          color: #475569;
          cursor: pointer;
        }

        .widget-preview-close:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        .widget-preview-toolbar {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.65rem 1.5rem;
          background: #f8fafc;
          color: #64748b;
          font-size: 0.72rem;
        }

        .widget-preview-toolbar span:first-child {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          color: #166534;
          font-weight: 700;
        }

        .preview-status-dot {
          width: 0.45rem;
          height: 0.45rem;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 0 0 3px #dcfce7;
        }

        .widget-preview-frame-wrap {
          min-height: 520px;
          overflow: auto;
          padding: 1rem;
          background: #eef2f7;
        }

        .widget-preview-frame {
          display: block;
          width: 100%;
          min-height: 560px;
          border: 0;
          border-radius: 0.85rem;
          background: #f8fafc;
        }

        @media (max-width: 1024px) {
          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .property-info {
            width: 100%;
          }

          .header-actions {
            width: 100%;
          }

          .property-settings-button,
          .property-widget-button {
            flex: 1;
            justify-content: center;
            width: auto;
          }

          .edit-button {
            width: 100%;
            justify-content: center;
          }

          .quick-info {
            flex-direction: column;
            gap: 1rem;
          }
        }

        @media (max-width: 768px) {
          .property-name {
            font-size: 1.25rem;
          }

          .tabs-wrapper {
            flex-wrap: nowrap;
            overflow-x: auto;
          }

          .tab-content {
            padding: 1rem;
          }

          .property-settings-backdrop {
            padding: 0.35rem;
          }

          .property-settings-content {
            padding: 0.75rem;
          }

          .property-settings-tabs {
            overflow-x: auto;
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }

          .header-actions {
            flex-direction: column;
          }

          .property-settings-button,
          .property-widget-button {
            width: 100%;
          }

          .property-settings-tab {
            white-space: nowrap;
          }

          .tabs-actions {
            justify-content: stretch;
          }

          .widget-preview-link {
            width: 100%;
            justify-content: center;
          }

          .widget-preview-toolbar {
            flex-direction: column;
            gap: 0.35rem;
          }

          .widget-preview-backdrop {
            padding: 0.5rem;
          }

          .widget-preview-frame-wrap {
            min-height: 440px;
            padding: 0.5rem;
          }

          .widget-preview-frame {
            min-height: 500px;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewProperty;
